import { useState, useRef, useEffect } from "react";
import { permissionsService } from "../../services/permissions.service";
import styles from "../../styles/components/AdminDetailModals.module.css";
import ModalOverlay from "../../ui/Modals/ModalOverlay";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/add.svg";
import { accountsService } from "../../services/accounts.service";
import Avatar from "../../ui/avatar/Avatar";
import AvatarUploadField from "../../ui/avatar/AvatarUploadField";
import GroupTag from "../../ui/tags/GroupTag";
import { getGrupo } from "../../utils/getGrupo";
import { customFieldsService } from "../../services/customFields.service";
import { usersService } from "../../services/users.service";
import Button from "../../ui/buttons/Button";
import HoverIconButton from "../../ui/buttons/HoverIconButton";
import StatusBadge from "../../ui/tags/StatusBadge";
import DropdownSelect from "../../ui/inputs/DropdownSelect";
import { AddCustomFieldDefForm } from "../../ui/forms/CustomFieldForms";
import TimezoneSelect, { useTimezoneLabel } from "../../ui/inputs/TimezoneSelect";
import BusinessHoursEditor, {
  normalizeBusinessHours,
  validateBusinessHours,
} from "../../ui/businessHours/BusinessHoursEditor";
import { extractApiErrorMessage } from "../../utils/apiError";
import FloatingModal from "../../ui/Modals/FloatingModal";
import Toggle from "../../ui/selectors/Toggle";


const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Texto" },
  { value: "number", label: "Número" },
  { value: "date", label: "Fecha" },
  { value: "boolean", label: "Booleano" },
];



function FieldRow({ label, children, centered = false }) {
  return (
    <div
      className={`${styles.fieldRow} ${centered ? styles.fieldRowCentered : ""}`}
    >
      <span className={styles.fieldLabel}>{label}</span>
      <div className={styles.fieldValue}>{children}</div>
    </div>
  );
}

function AccountItem({ account, isEditing, onRemove }) {
  const logo = account.photo ?? account.logo ?? account.avatar_url ?? account.logo_url ?? null;

  return (
    <div className={`${styles.accountItem} ${HoverIconButton.rowClassName}`}>
      <Avatar src={logo} name={account.name} size={36} objectFit="contain" />
      <div className={styles.accountDetails}>
        <div className={styles.accountName}>{account.name}</div>
        {account.email && (
          <div className={styles.accountEmail}>{account.email}</div>
        )}
      </div>
      {isEditing && onRemove && (
        <HoverIconButton icon={<TrashIcon />} onClick={onRemove} aria-label="Deslinkear cuenta" />
      )}
    </div>
  );
}


function getLinkedAccounts(user) {
  const raw = user.linked_accounts ?? user.accounts ?? (user.account ? [user.account] : []);
  if (!Array.isArray(raw)) return [];
  return raw.map((a) => ({
    id: a.account_id ?? a.id,
    name: a.account_name ?? a.name,
    photo: a.account_avatar_url ?? a.photo ?? a.logo ?? a.avatar_url ?? a.logo_url ?? null,
    email: a.mail_accounts?.[0]?.email ?? null,
    isAdmin: a.is_account_admin ?? false,
    userAccountId: a.user_account_id ?? a.ua_id ?? null,
    permissionGroups: Array.isArray(a.permission_groups) ? a.permission_groups : [],
  }));
}

export function UserDetailModal({ user, onClose, onDeleted, startEditing = false, createMode = false, onCreated, onUserChanged }) {
  const startEditingRef = useRef(startEditing);
  startEditingRef.current = startEditing;

  const [isEditing, setIsEditing] = useState(false);
  const [addedAccounts, setAddedAccounts] = useState([]);
  const [removedAccountIds, setRemovedAccountIds] = useState(new Set());
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [addingAccount, setAddingAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [groupMemberships, setGroupMemberships] = useState([]);
  const [showAssignGroup, setShowAssignGroup] = useState(false);
  const [assignUaId, setAssignUaId] = useState("");
  const [assignGroupId, setAssignGroupId] = useState("");
  const [accountGroups, setAccountGroups] = useState([]);
  const [assigningGroup, setAssigningGroup] = useState(false);
  const [actionError, setActionError] = useState(null);

  // create mode state
  const [createFirstName, setCreateFirstName] = useState("");
  const [createLastName, setCreateLastName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createCuentaId, setCreateCuentaId] = useState("");
  const [createAccounts, setCreateAccounts] = useState([]);
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // null | "checking" | "AVAILABLE" | "EXISTS_IN_ACCOUNT" | "EXISTS_EXTERNAL"
  const [createPermGroups, setCreatePermGroups] = useState([]);
  const [createPermGroupId, setCreatePermGroupId] = useState("");

  useEffect(() => {
    setAddedAccounts([]);
    setRemovedAccountIds(new Set());
    setIsEditing(startEditingRef.current);
    setShowAddAccount(false);
    setShowDeleteConfirm(false);
    setGroupMemberships([]);
    setShowAssignGroup(false);
    setAssignUaId("");
    setAssignGroupId("");
    setAccountGroups([]);
    setActionError(null);
  }, [user?.id, user?.first_name, user?.last_name, user?.email]);

  useEffect(() => {
    if (!user?.id) return;
    const memberships = getLinkedAccounts(user)
      .filter((acc) => acc.userAccountId != null)
      .flatMap((acc) =>
        acc.permissionGroups.map((g) => ({
          groupId: g.id,
          groupName: g.name,
          accountId: acc.id,
          accountName: acc.name,
          uaId: acc.userAccountId,
        })),
      );
    setGroupMemberships(memberships);
  }, [user]);

  useEffect(() => {
    if (!createMode) return;
    setCreateFirstName("");
    setCreateLastName("");
    setCreateEmail("");
    setCreateCuentaId("");
    setCreateError(null);
    setEmailStatus(null);
    setCreatePermGroups([]);
    setCreatePermGroupId("");
    accountsService.list().then(setCreateAccounts).catch(() => { });
  }, [createMode]);

  if (!user && !createMode) return null;

  const firstName = user?.first_name?.trim() ?? "";
  const lastName = user?.last_name?.trim() ?? "";
  const fullName = `${firstName} ${lastName}`.trim() || user?.username || "-";
  const grupo = user ? getGrupo(user) : "-";
  const isActive = user?.is_active !== false;
  const linkedAccounts = user
    ? [...getLinkedAccounts(user), ...addedAccounts].filter((a) => !removedAccountIds.has(String(a.id)))
    : [];

  const handleClose = () => {
    setIsEditing(false);
    setShowAddAccount(false);
    setShowDeleteConfirm(false);
    setActionError(null);
    onClose();
  };

  const handleDeleteUser = async () => {
    setActionError(null);
    try {
      await usersService.remove(user.id);
      onDeleted?.(user.id);
      handleClose();
    } catch (e) {
      console.error(e);
      setShowDeleteConfirm(false);
      setActionError(
        extractApiErrorMessage(e) || "No se pudo eliminar el usuario.",
      );
    }
  };

  // Los datos personales solo se cargan al crear el usuario: en edición se
  // gestionan cuentas y grupos, que persisten al instante, así que salir del
  // modo edición no requiere guardar nada.
  const handleFinishEditing = () => {
    setIsEditing(false);
  };

  const handleRemoveAccount = async (accountId) => {
    setActionError(null);
    try {
      await accountsService.removeUser(accountId, user.id);
      setRemovedAccountIds((prev) => new Set([...prev, String(accountId)]));
    } catch (e) {
      console.error(e);
      setActionError(
        extractApiErrorMessage(e) || "No se pudo quitar la cuenta del usuario.",
      );
    }
  };

  const handleEmailBlur = async () => {
    const email = createEmail.trim();
    if (!email || !email.includes("@")) return;
    setEmailStatus("checking");
    try {
      const data = await accountsService.checkUserEmail(email);
      setEmailStatus(data.code ?? null);
    } catch {
      setEmailStatus(null);
    }
  };

  const handleCreateAccountChange = async (v) => {
    setCreateCuentaId(v);
    setCreateError(null);
    setCreatePermGroupId("");
    if (!v) { setCreatePermGroups([]); return; }
    try {
      const groups = await permissionsService.listGroups(Number(v));
      setCreatePermGroups(Array.isArray(groups) ? groups : []);
    } catch { setCreatePermGroups([]); }
  };

  const handleCreateUser = async () => {
    if (!createEmail.trim() || !createCuentaId) {
      setCreateError("Completá el email y la cuenta antes de continuar.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const extras = {};
      if (createFirstName.trim()) extras.first_name = createFirstName.trim();
      if (createLastName.trim()) extras.last_name = createLastName.trim();
      const data = await accountsService.findOrCreateUser(Number(createCuentaId), createEmail.trim(), extras);
      const code = data.code;
      if (code === "EXISTS_EXTERNAL") {
        setCreateError(data.message ?? "El usuario no pertenece a ninguna de tus cuentas.");
        return;
      }
      const created = data.user ?? data;
      if (createPermGroupId) {
        try {
          const ua = await permissionsService.findUserAccount(created.id, Number(createCuentaId));
          if (ua) await permissionsService.assignGroup(ua.id, { group_id: Number(createPermGroupId) });
        } catch (e) {
          console.error(e);
        }
      }
      onCreated?.(created, code);
    } catch (e) {
      console.error(e);
      setCreateError("Ocurrió un error al crear el usuario.");
    } finally {
      setCreating(false);
    }
  };

  const createAccountOptions = createAccounts.map((a) => ({ value: String(a.id), label: a.name ?? "-" }));
  const createPermGroupOptions = createPermGroups.map((g) => ({ value: String(g.id), label: g.name }));

  const handleOpenAddAccount = async () => {
    setShowAddAccount(true);
    setSelectedAccountId("");
    try {
      const all = await accountsService.list();
      const linkedIds = new Set(linkedAccounts.map((a) => String(a.id)));
      setAvailableAccounts(all.filter((a) => !linkedIds.has(String(a.id))));
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddAccount = async () => {
    if (!selectedAccountId) return;
    setAddingAccount(true);
    setActionError(null);
    try {
      await accountsService.addUser(Number(selectedAccountId), user.id, false);
      const added = availableAccounts.find((a) => String(a.id) === String(selectedAccountId));
      if (added) {
        setAddedAccounts((prev) => [
          ...prev,
          { id: added.id, name: added.name, photo: added.photo ?? null, isAdmin: false },
        ]);
      }
      setShowAddAccount(false);
      setSelectedAccountId("");
    } catch (e) {
      console.error(e);
      setActionError(
        extractApiErrorMessage(e) || "No se pudo agregar la cuenta al usuario.",
      );
    } finally {
      setAddingAccount(false);
    }
  };

  const accountOptions = availableAccounts.map((a) => ({ value: String(a.id), label: a.name ?? "-" }));

  const groupedByAccount = linkedAccounts
    .map((acc) => ({ acc, groups: groupMemberships.filter((m) => m.accountId === acc.id) }))
    .filter(({ groups }) => groups.length > 0);

  const linkedAccountsForAssign = linkedAccounts.filter((a) => a.userAccountId != null);
  const assignAccountOptions = linkedAccountsForAssign.map((a) => ({
    value: String(a.userAccountId),
    label: a.name ?? "-",
  }));
  const assignGroupOptions = accountGroups.map((g) => ({ value: String(g.id), label: g.name }));

  const handleAssignUaIdChange = async (uaId) => {
    setAssignUaId(uaId);
    setAssignGroupId("");
    if (!uaId) { setAccountGroups([]); return; }
    const acc = linkedAccountsForAssign.find((a) => String(a.userAccountId) === String(uaId));
    if (!acc) return;
    try {
      const groups = await permissionsService.listGroups(acc.id);
      const existingIds = new Set(
        groupMemberships.filter((m) => m.accountId === acc.id).map((m) => m.groupId),
      );
      const available = (Array.isArray(groups) ? groups : []).filter((g) => !existingIds.has(g.id));
      setAccountGroups(available);
    } catch { setAccountGroups([]); }
  };

  const buildUpdatedUser = (memberships) => {
    if (!user) return null;
    const groupsByAccount = {};
    memberships.forEach((m) => {
      if (!groupsByAccount[m.accountId]) groupsByAccount[m.accountId] = [];
      groupsByAccount[m.accountId].push({ id: m.groupId, name: m.groupName });
    });
    return {
      ...user,
      linked_accounts: user.linked_accounts?.map((a) => {
        const aId = a.account_id ?? a.id;
        return { ...a, permission_groups: groupsByAccount[aId] ?? [] };
      }),
    };
  };

  const handleAssignGroup = async () => {
    if (!assignUaId || !assignGroupId) return;
    setAssigningGroup(true);
    setActionError(null);
    try {
      await permissionsService.assignGroup(Number(assignUaId), { group_id: Number(assignGroupId) });
      const group = accountGroups.find((g) => String(g.id) === String(assignGroupId));
      const acc = linkedAccountsForAssign.find((a) => String(a.userAccountId) === String(assignUaId));
      if (group && acc) {
        const newMemberships = [
          ...groupMemberships,
          { groupId: group.id, groupName: group.name, accountId: acc.id, accountName: acc.name, uaId: Number(assignUaId) },
        ];
        setGroupMemberships(newMemberships);
        onUserChanged?.(buildUpdatedUser(newMemberships));
      }
      setShowAssignGroup(false);
      setAssignUaId("");
      setAssignGroupId("");
      setAccountGroups([]);
    } catch (e) {
      console.error(e);
      setActionError(
        extractApiErrorMessage(e) || "No se pudo asignar el grupo.",
      );
    } finally {
      setAssigningGroup(false);
    }
  };

  const handleRemoveGroup = async (uaId, groupId) => {
    setActionError(null);
    try {
      await permissionsService.removeFromGroup(uaId, groupId);
      const newMemberships = groupMemberships.filter((m) => !(m.uaId === uaId && m.groupId === groupId));
      setGroupMemberships(newMemberships);
      onUserChanged?.(buildUpdatedUser(newMemberships));
    } catch (e) {
      console.error(e);
      setActionError(
        extractApiErrorMessage(e) || "No se pudo quitar el grupo.",
      );
    }
  };

  return (
    <ModalOverlay className={styles.overlay} onClose={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div className={styles.modalHeader}>
          <div className={styles.headerInfo}>
            <div className={styles.modalName}>{createMode ? "Nuevo usuario" : fullName}</div>
            {!createMode && user && (
              <div className={styles.modalUsername}>
                <span>{user.username}</span>
                <StatusBadge isActive={isActive} size={20} />
              </div>
            )}
          </div>
          {!createMode && isEditing && (
            <button
              type="button"
              className={styles.trashBtn}
              aria-label="Eliminar usuario"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <TrashIcon className={styles.trashIcon} />
            </button>
          )}
        </div>

        <div className={styles.modalFields}>
          {createMode ? (
            <>
              <FieldRow label="Nombre">
                <input
                  className={styles.fieldInput}
                  placeholder="Nombre"
                  value={createFirstName}
                  onChange={(e) => setCreateFirstName(e.target.value)}
                  autoFocus
                />
              </FieldRow>
              <FieldRow label="Apellido">
                <input
                  className={styles.fieldInput}
                  placeholder="Apellido"
                  value={createLastName}
                  onChange={(e) => setCreateLastName(e.target.value)}
                />
              </FieldRow>
              <FieldRow label="Email">
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <input
                    className={styles.fieldInput}
                    type="email"
                    placeholder="usuario@mail.com"
                    value={createEmail}
                    onChange={(e) => { setCreateEmail(e.target.value); setCreateError(null); setEmailStatus(null); }}
                    onBlur={handleEmailBlur}
                  />
                  {emailStatus === "checking" && (
                    <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Verificando...</span>
                  )}
                  {emailStatus === "AVAILABLE" && (
                    <span style={{ fontSize: 12, color: "var(--color-green-500, #12B76A)" }}>Email disponible</span>
                  )}
                  {emailStatus === "EXISTS_IN_ACCOUNT" && (
                    <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Ya existe en tus cuentas — se asignará a la cuenta seleccionada</span>
                  )}
                  {emailStatus === "EXISTS_EXTERNAL" && (
                    <span style={{ fontSize: 12, color: "var(--surface-error)" }}>Este email pertenece a otra organización</span>
                  )}
                </div>
              </FieldRow>
              <FieldRow label="Cuenta">
                <DropdownSelect
                  placeholder="Seleccionar cuenta..."
                  options={createAccountOptions}
                  value={createCuentaId}
                  onChange={handleCreateAccountChange}
                />
              </FieldRow>
              <FieldRow label="Grupo">
                <DropdownSelect
                  placeholder={createPermGroupOptions.length ? "Sin grupo" : "Seleccioná una cuenta primero"}
                  options={createPermGroupOptions}
                  value={createPermGroupId}
                  onChange={(v) => setCreatePermGroupId(v)}
                />
              </FieldRow>
              {createError && (
                <p className={styles.centerModalMessage} style={{ margin: "8px 0 0" }}>{createError}</p>
              )}
            </>
          ) : (
            <>
              {/* Nombre, apellido y email solo se completan al crear el
                  usuario; en edición son de solo lectura. */}
              <FieldRow label="Nombre">
                <span className={styles.fieldText}>{firstName || "-"}</span>
              </FieldRow>

              <FieldRow label="Apellido">
                <span className={styles.fieldText}>{lastName || "-"}</span>
              </FieldRow>

              <FieldRow label="Email">
                <span className={styles.fieldText}>{user?.email || "-"}</span>
              </FieldRow>

              <FieldRow label="Grupo">
                <GroupTag name={groupMemberships[0]?.groupName || grupo} />
              </FieldRow>
            </>
          )}
        </div>

        {!createMode && (
          <>
            <div className={styles.accountsSection}>
              <div className={styles.accountsSectionTitle}>Cuentas linkeadas</div>

              <div className={styles.accountsList}>
                {linkedAccounts.length ? (
                  linkedAccounts.map((acc, i) => (
                    <AccountItem
                      key={acc.id ?? i}
                      account={acc}
                      isEditing={isEditing}
                      onRemove={() => handleRemoveAccount(acc.id)}
                    />
                  ))
                ) : (
                  <div className={styles.noAccounts}>Sin cuentas linkeadas</div>
                )}
              </div>

              {isEditing && (
                <div className={styles.addAccountSection}>
                  {showAddAccount ? (
                    <div className={styles.addFieldForm}>
                      <DropdownSelect
                        placeholder="Seleccionar cuenta..."
                        options={accountOptions}
                        value={selectedAccountId}
                        onChange={(v) => setSelectedAccountId(v)}
                      />
                      <div className={styles.addFieldActions}>
                        <button
                          type="button"
                          className={styles.saveBtn}
                          onClick={handleAddAccount}
                          disabled={addingAccount || !selectedAccountId}
                        >
                          {addingAccount ? "Agregando..." : "Agregar"}
                        </button>
                        <button
                          type="button"
                          className={styles.editBtn}
                          onClick={() => { setShowAddAccount(false); setSelectedAccountId(""); }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className={styles.addAccountBtn} onClick={handleOpenAddAccount}>
                      <AddIcon className={styles.addIcon} />
                      Agregar cuenta
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className={styles.accountsSection}>
              <div className={styles.accountsSectionTitle}>Grupos de permisos</div>

              <div className={styles.accountsList}>
                {groupedByAccount.length === 0 ? (
                  <div className={styles.noAccounts}>Sin grupos asignados</div>
                ) : (
                  groupedByAccount.map(({ acc, groups }, index) => (
                    <div
                      key={acc.id}
                      style={index < groupedByAccount.length - 1 ? { marginBottom: 14 } : {}}
                    >
                      {index > 0 && (
                        <hr style={{ border: "none", borderTop: "1px solid var(--stroke-add-new)", margin: "0 204px 0 52px" }} />
                      )}
                      <div className={styles.accountItem} style={{ pointerEvents: "none", paddingTop: 6, paddingBottom: 4 }}>
                        <Avatar src={acc.photo} name={acc.name} size={36} objectFit="contain" />
                        <div className={styles.accountDetails}>
                          <div className={styles.accountName}>{acc.name}</div>
                        </div>
                      </div>
                      {groups.map((m) => (
                        <div key={m.groupId} className={`${styles.groupItem} ${HoverIconButton.rowClassName}`}>
                          <span style={{ flex: 1, fontSize: 13, color: "var(--text-secondary)", fontFamily: "DM Sans" }}>
                            {m.groupName}
                          </span>
                          {isEditing && (
                            <HoverIconButton icon={<TrashIcon />} size={18} onClick={() => handleRemoveGroup(m.uaId, m.groupId)} aria-label="Quitar grupo" />
                          )}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>

              {isEditing && linkedAccountsForAssign.length > 0 && (
                <div className={styles.addAccountSection}>
                  {showAssignGroup ? (
                    <div className={styles.addFieldForm}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <DropdownSelect
                            placeholder="Cuenta..."
                            options={assignAccountOptions}
                            value={assignUaId}
                            onChange={handleAssignUaIdChange}
                          />
                        </div>
                        {assignUaId && (
                          <div style={{ flex: 1 }}>
                            <DropdownSelect
                              placeholder="Grupo..."
                              options={assignGroupOptions}
                              value={assignGroupId}
                              onChange={(v) => setAssignGroupId(v)}
                            />
                          </div>
                        )}
                      </div>
                      <div className={styles.addFieldActions}>
                        <button
                          type="button"
                          className={styles.saveBtn}
                          onClick={handleAssignGroup}
                          disabled={assigningGroup || !assignUaId || !assignGroupId}
                        >
                          {assigningGroup ? "Asignando..." : "Asignar"}
                        </button>
                        <button
                          type="button"
                          className={styles.editBtn}
                          onClick={() => { setShowAssignGroup(false); setAssignUaId(""); setAssignGroupId(""); setAccountGroups([]); }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className={styles.addAccountBtn} onClick={() => setShowAssignGroup(true)}>
                      <AddIcon className={styles.addIcon} />
                      Asignar grupo
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {!createMode && actionError && (
          <div onClick={(e) => e.stopPropagation()}>
            <FloatingModal direction="column">
              <span role="alert" style={{ whiteSpace: "pre-line" }}>
                {actionError}
              </span>
              <Button onClick={() => setActionError(null)}>OK</Button>
            </FloatingModal>
          </div>
        )}

        <div className={styles.modalFooter}>
          {createMode ? (
            <>
              <button
                type="button"
                className={styles.saveBtn}
                onClick={handleCreateUser}
                disabled={creating || emailStatus === "EXISTS_EXTERNAL" || emailStatus === "checking"}
              >
                {creating ? "Creando..." : "Crear usuario"}
              </button>
              <button
                type="button"
                className={styles.editBtn}
                style={{ marginTop: 8 }}
                onClick={handleClose}
              >
                Cancelar
              </button>
            </>
          ) : isEditing ? (
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleFinishEditing}
            >
              Listo
            </button>
          ) : (
            <Button
              variant="secondary"
              tone="blue"
              iconLeft={<EditIcon className={styles.editIcon} />}
              fullWidth
              height={48}
              onClick={() => setIsEditing(true)}
            >
              Editar información
            </Button>
          )}
        </div>
      </div>

      {!createMode && showDeleteConfirm && (
        <ConfirmDeleteModal
          title="¿Deseas eliminar este usuario?"
          description="Eliminar este usuario definitivamente no tiene vuelta atrás."
          name={user?.username}
          onConfirm={handleDeleteUser}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}
    </ModalOverlay>
  );
}

export function CreateAccountModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    accountsService.list().then(setAccounts).catch(() => { });
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload = { name: name.trim() };
      const data = parentId
        ? await accountsService.createChild(Number(parentId), payload)
        : await accountsService.create(payload);
      onCreated?.(data);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const accountOptions = accounts.map((a) => ({ value: String(a.id), label: a.name ?? "-" }));

  return (
    <ModalOverlay className={styles.centerOverlay} onClose={onClose}>
      <div className={styles.centerModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.centerModalTitle}>Nueva cuenta</div>

        <div className={styles.centerModalFields}>
          <div className={styles.centerModalField}>
            <label className={styles.centerModalLabel}>Nombre</label>
            <input
              className={styles.fieldInput}
              placeholder="Nombre de la cuenta"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
              autoFocus
            />
          </div>
          <div className={styles.centerModalField}>
            <label className={styles.centerModalLabel}>Cuenta padre (opcional)</label>
            <DropdownSelect
              placeholder="Sin cuenta padre (raíz)"
              options={accountOptions}
              value={parentId}
              onChange={(v) => setParentId(v)}
            />
          </div>
        </div>

        <div className={styles.centerModalActions}>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleCreate}
            disabled={saving || !name.trim()}
          >
            {saving ? "Creando..." : "Crear cuenta"}
          </button>
          <button type="button" className={styles.editBtn} onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

export function CreateUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    accountsService.list().then(setAccounts).catch(() => { });
  }, []);

  const handleCreate = async () => {
    if (!email.trim() || !accountId) return;
    setSaving(true);
    setErrorMessage(null);
    try {
      const data = await accountsService.findOrCreateUser(Number(accountId), email.trim());
      const code = data.code;

      if (code === "EXISTS_EXTERNAL") {
        setErrorMessage(data.message ?? "El usuario ya existe en el sistema pero no pertenece a ninguna de tus cuentas.");
        return;
      }

      // CREATED o EXISTS_IN_ACCOUNT → abrir modal con el usuario
      const user = data.user ?? data;
      onCreated?.(user, code);
      onClose();
    } catch (e) {
      console.error(e);
      setErrorMessage("Ocurrió un error al procesar la solicitud.");
    } finally {
      setSaving(false);
    }
  };

  const accountOptions = accounts.map((a) => ({ value: String(a.id), label: a.name ?? "-" }));

  return (
    <ModalOverlay className={styles.centerOverlay} onClose={onClose}>
      <div className={styles.centerModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.centerModalTitle}>Nuevo usuario</div>

        <div className={styles.centerModalFields}>
          <div className={styles.centerModalField}>
            <label className={styles.centerModalLabel}>Cuenta</label>
            <DropdownSelect
              placeholder="Seleccionar cuenta..."
              options={accountOptions}
              value={accountId}
              onChange={(v) => { setAccountId(v); setErrorMessage(null); }}
            />
          </div>
          <div className={styles.centerModalField}>
            <label className={styles.centerModalLabel}>Email</label>
            <input
              className={styles.fieldInput}
              type="email"
              placeholder="usuario@mail.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErrorMessage(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
            />
          </div>
          {errorMessage && (
            <p className={styles.centerModalMessage}>{errorMessage}</p>
          )}
        </div>

        <div className={styles.centerModalActions}>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleCreate}
            disabled={saving || !email.trim() || !accountId}
          >
            {saving ? "Procesando..." : "Crear usuario"}
          </button>
          <button type="button" className={styles.editBtn} onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function AdminListItem({ admin }) {
  const name = admin.name || admin.username || "-";
  const photo = admin.photo ?? admin.avatar_url ?? null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <Avatar src={photo} name={name} size={32} />
      <span className={styles.fieldText}>{name}</span>
    </div>
  );
}

function ReferenceField({ value }) {
  if (!value || value === "-") return <span className={styles.fieldText}>-</span>;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <path d="M3 11L11 3M11 3H5.5M11 3V8.5" stroke="var(--text-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className={styles.fieldText}>{value}</span>
    </div>
  );
}

export function AccountDetailModal({ account, accounts = [], onClose, onSaved, onDeleted }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [deletePhoto, setDeletePhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [showAddField, setShowAddField] = useState(false);
  const [addingField, setAddingField] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [timezone, setTimezone] = useState("");
  const [businessHours, setBusinessHours] = useState([]);
  const [showHoursErrors, setShowHoursErrors] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [privateProspecting, setPrivateProspecting] = useState(true);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [privacyError, setPrivacyError] = useState(null);

  useEffect(() => {
    if (!account?.id) return;
    setEditName(account.name ?? "");
    setPendingPhoto(null);
    setDeletePhoto(false);
    setIsEditing(false);
    setShowAddField(false);
    setTimezone(account.timezone ?? "");
    setBusinessHours(normalizeBusinessHours(account.business_hours));
    setShowHoursErrors(false);
    setSaveError(null);
    setPrivateProspecting(account.private_prospecting_data ?? true);
    setPrivacyError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id, account?.name]);

  useEffect(() => {
    if (!account?.id) return;
    customFieldsService
      .fetchCustomFields(account.id)
      .then((data) => {
        const fields = Array.isArray(data) ? data : (data?.results ?? []);
        setCustomFields(fields);
      })
      .catch(() => { });
  }, [account?.id]);

  const timezoneLabel = useTimezoneLabel(timezone);

  if (!account) return null;

  const fatherAccountId = typeof account.father_account === "number"
    ? account.father_account
    : (account.father_account?.id ?? null);
  const reference = account.father_account?.name
    ?? (fatherAccountId != null ? accounts.find((a) => a.id === fatherAccountId)?.name : null)
    ?? null;
  const photoSrc = pendingPhoto?.previewUrl ?? (deletePhoto ? null : (account.photo ?? account.avatar_url ?? null));
  const admins = Array.isArray(account.admins) ? account.admins : [];

  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  const hoursValidation = validateBusinessHours(businessHours);

  const handleSave = async () => {
    if (!timezone || !hoursValidation.valid) {
      setShowHoursErrors(true);
      setSaveError(
        !timezone ? "La zona horaria es obligatoria." : hoursValidation.message,
      );
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      // El array de horarios va anidado en JSON; la foto se manda aparte con
      // FormData (un PATCH parcial solo cuando cambió la imagen).
      let data = await accountsService.update(account.id, {
        name: editName,
        timezone,
        business_hours: businessHours,
      });

      if (pendingPhoto?.file) {
        const fd = new FormData();
        fd.append("photo", pendingPhoto.file);
        data = await accountsService.update(account.id, fd);
      } else if (deletePhoto) {
        const fd = new FormData();
        fd.append("photo", "");
        data = await accountsService.update(account.id, fd);
      }

      onSaved?.(data);
      setIsEditing(false);
      setPendingPhoto(null);
      setDeletePhoto(false);
      setShowHoursErrors(false);
    } catch (e) {
      setSaveError(
        extractApiErrorMessage(e) || "No se pudo guardar la cuenta.",
      );
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handlePrivateProspectingChange = async (nextValue) => {
    if (!privateProspecting && nextValue && !window.confirm("¿Desactivar el uso compartido de contactos verificados?")) {
      return;
    }
    setPrivateProspecting(nextValue);
    setSavingPrivacy(true);
    setPrivacyError(null);
    try {
      const data = await accountsService.update(account.id, {
        private_prospecting_data: nextValue,
      });
      onSaved?.(data);
    } catch (e) {
      setPrivateProspecting(!nextValue);
      setPrivacyError(
        extractApiErrorMessage(e) || "No se pudo actualizar la configuración.",
      );
      console.error(e);
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleDeleteConfirm = async () => {
    await accountsService.remove(account.id);
    onDeleted?.(account.id);
    handleClose();
  };

  const handleAddField = async (name, type) => {
    if (!name.trim()) return;
    setAddingField(true);
    try {
      const code = name.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
      const field = await customFieldsService.createForAccount({
        account: account.id,
        name: name.trim(),
        code,
        data_type: type,
      });
      setCustomFields((prev) => [...prev, field]);
      setShowAddField(false);
    } catch (e) {
      console.error(e);
    } finally {
      setAddingField(false);
    }
  };

  const handleDeleteField = async (fieldId) => {
    try {
      await customFieldsService.removeField(fieldId);
      setCustomFields((prev) => prev.filter((f) => f.id !== fieldId));
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <ModalOverlay className={styles.overlay} onClose={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div className={styles.modalHeader}>
          <div className={styles.headerInfo}>
            <div className={styles.modalName}>{account.name ?? "-"}</div>
            {reference && (
              <div className={styles.modalUsername}>
                <span>{reference}</span>
              </div>
            )}
          </div>
          {isEditing && (
            <button type="button" className={styles.trashBtn} aria-label="Eliminar cuenta" onClick={() => setShowDeleteConfirm(true)}>
              <TrashIcon className={styles.trashIcon} />
            </button>
          )}
        </div>

        <div className={styles.modalFields}>
          <FieldRow label="Nombre">
            {isEditing ? (
              <input
                className={styles.fieldInput}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            ) : (
              <span className={styles.fieldText}>{account.name ?? "-"}</span>
            )}
          </FieldRow>

          {reference != null && (
            <FieldRow label="Referencia">
              <ReferenceField value={reference} />
            </FieldRow>
          )}

          <FieldRow label="Pill">
            <span title={account.name ?? "-"} className={styles.pill}>
              {account.name ?? "-"}
            </span>
          </FieldRow>

          {fatherAccountId == null && (
            <FieldRow label="Datos de prospección privados" centered>
              <div
                className={styles.privacyValue}
                title={
                  "Aplica a esta cuenta y a todas sus cuentas hijas. Con la " +
                  "opción activada, los contactos no se comparten con la base " +
                  "global de la aplicación."
                }
              >
                <Toggle
                  checked={privateProspecting}
                  onChange={handlePrivateProspectingChange}
                  disabled={savingPrivacy}
                  ariaLabel="Datos de prospección privados"
                />
                <span className={styles.fieldText}>
                  {privateProspecting ? "Activados" : "Desactivados"}
                </span>
              </div>
              {privacyError && (
                <div className={styles.formError} role="alert">
                  {privacyError}
                </div>
              )}
            </FieldRow>
          )}

          <FieldRow label="Foto Miniatura">
            <AvatarUploadField
              src={photoSrc}
              isEditing={isEditing}
              shape="square"
              onChange={(file, previewUrl) => setPendingPhoto({ file, previewUrl })}
              onDelete={() => { setPendingPhoto(null); setDeletePhoto(true); }}
            />
          </FieldRow>

          <FieldRow label="Supervisor">
            <div className={styles.adminStack}>
              {admins.length ? (
                admins.map((admin) => (
                  <AdminListItem key={admin.id ?? admin.username} admin={admin} />
                ))
              ) : (
                <span className={styles.fieldText}>-</span>
              )}
            </div>
          </FieldRow>

          {customFields.map((field) => (
            <FieldRow key={field.id} label={field.name}>
              <div className={HoverIconButton.rowClassName} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className={styles.fieldText}>
                  {FIELD_TYPE_OPTIONS.find((o) => o.value === field.data_type)?.label ?? field.data_type ?? "-"}
                </span>
                {isEditing && (
                  <HoverIconButton icon={<TrashIcon />} onClick={() => handleDeleteField(field.id)} aria-label="Eliminar campo" />
                )}
              </div>
            </FieldRow>
          ))}
        </div>

        <div className={styles.addDatoWrapper}>
          {showAddField ? (
            <AddCustomFieldDefForm
              saving={addingField}
              onAdd={handleAddField}
              onCancel={() => setShowAddField(false)}
            />
          ) : (
            <button type="button" className={styles.addAccountBtn} onClick={() => setShowAddField(true)}>
              <AddIcon className={styles.addIcon} />
              Agregar dato
            </button>
          )}
        </div>

        <div className={styles.businessHoursSection}>
          <div className={styles.businessHoursTitle}>Zona horaria</div>
          {isEditing ? (
            <TimezoneSelect
              label={null}
              value={timezone}
              onChange={setTimezone}
              required
              error={
                showHoursErrors && !timezone
                  ? "La zona horaria es obligatoria"
                  : undefined
              }
            />
          ) : (
            <span className={styles.fieldText}>{timezoneLabel || "-"}</span>
          )}

          <div className={styles.businessHoursTitle} style={{ marginTop: 20 }}>
            Horarios laborales
          </div>
          {isEditing ? (
            <BusinessHoursEditor
              value={businessHours}
              onChange={setBusinessHours}
              errors={showHoursErrors ? hoursValidation.rowErrors : undefined}
            />
          ) : (
            <BusinessHoursEditor value={businessHours} readOnly />
          )}
        </div>

        <div className={styles.modalFooter}>
          {saveError && (
            <div className={styles.formError} role="alert">
              {saveError}
            </div>
          )}
          {isEditing ? (
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Guardando..." : "Guardar información"}
            </button>
          ) : (
            <Button
              variant="secondary"
              tone="blue"
              iconLeft={<EditIcon className={styles.editIcon} />}
              fullWidth
              height={48}
              onClick={() => setIsEditing(true)}
            >
              Editar información
            </Button>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <ConfirmDeleteModal
          title="¿Deseas eliminar esta cuenta?"
          description="Eliminar esta cuenta definitivamente no tiene vuelta atrás."
          name={account.name}
          onConfirm={handleDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}
    </ModalOverlay>
  );
}



function SmallCheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      <path d="M2.5 7l3.5 3.5 5.5-5.5" stroke="var(--color-brand-600)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function PermissionGroupDetailModal({ group, createMode = false, accountId, onClose, onSaved, onCreated, onDeleted }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [showAddPerm, setShowAddPerm] = useState(false);
  const [pendingPermIds, setPendingPermIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    if (!group && !createMode) return;
    setShowAddPerm(false);
    setPendingPermIds(new Set());
    setShowDeleteConfirm(false);
    setActionError(null);
    const loadPerms = accountId ? permissionsService.listPermissions(accountId) : Promise.resolve([]);
    if (createMode && !group) {
      setName("");
      setDescription("");
      setPermissions([]);
      loadPerms.then(setAllPerms).catch(() => { });
    } else if (group?.id) {
      setLoading(true);
      Promise.all([permissionsService.getGroup(group.id), loadPerms])
        .then(([detail, perms]) => {
          setName(detail.name ?? "");
          setDescription(detail.description ?? "");
          setPermissions(detail.permissions ?? []);
          setAllPerms(perms);
        })
        .catch(() => { })
        .finally(() => setLoading(false));
    }
  }, [group, createMode, accountId]);

  if (!group && !createMode) return null;

  const inGroupIds = new Set(permissions.map((p) => p.id));
  const availablePerms = allPerms.filter((p) => !inGroupIds.has(p.id));

  const togglePending = (id) => {
    setPendingPermIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddSelected = () => {
    const toAdd = availablePerms.filter((p) => pendingPermIds.has(p.id));
    setPermissions((prev) => [...prev, ...toAdd]);
    setShowAddPerm(false);
    setPendingPermIds(new Set());
  };

  const handleRemovePerm = (id) => {
    setPermissions((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    setActionError(null);
    try {
      const permIds = permissions.map((p) => p.id);
      if (createMode && !group) {
        const created = await permissionsService.createGroup({
          name: name.trim(),
          description: description.trim(),
          account_id: accountId,
          permission_ids: permIds,
        });
        onCreated?.(created);
      } else {
        const updated = await permissionsService.updateGroup(group.id, {
          name: name.trim(),
          description: description.trim(),
          permission_ids: permIds,
        });
        onSaved?.(updated);
      }
    } catch (e) {
      console.error(e);
      setActionError(
        extractApiErrorMessage(e) || "No se pudo guardar el grupo.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setActionError(null);
    try {
      await permissionsService.deleteGroup(group.id);
      onDeleted?.(group.id);
      onClose();
    } catch (e) {
      console.error(e);
      setShowDeleteConfirm(false);
      setActionError(
        extractApiErrorMessage(e) || "No se pudo eliminar el grupo.",
      );
    }
  };

  const isCreate = createMode && !group;

  return (
    <ModalOverlay className={styles.overlay} onClose={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerInfo} style={{ flex: 1, minWidth: 0 }}>
            <input
              className={styles.groupNameInput}
              placeholder="Nombre del grupo"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus={isCreate}
            />
            <input
              className={styles.groupDescInput}
              placeholder="Descripción (opcional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          {!isCreate && (
            <button
              type="button"
              className={styles.trashBtn}
              aria-label="Eliminar grupo"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <TrashIcon className={styles.trashIcon} />
            </button>
          )}
        </div>

        {loading ? (
          <div className={styles.noAccounts} style={{ padding: "0 36px" }}>Cargando...</div>
        ) : (
          <div className={styles.accountsSection}>
            <div className={styles.accountsList}>
              {permissions.length === 0 && (
                <div className={styles.noAccounts}>Sin permisos asignados</div>
              )}
              {permissions.map((perm) => (
                <div key={perm.id} className={`${styles.accountItem} ${HoverIconButton.rowClassName}`}>
                  <div className={styles.accountDetails}>
                    <div className={styles.accountName}>{perm.name}</div>
                    {perm.description && (
                      <div className={styles.accountEmail}>{perm.description}</div>
                    )}
                  </div>
                  <HoverIconButton icon={<TrashIcon />} onClick={() => handleRemovePerm(perm.id)} aria-label="Quitar permiso" />
                </div>
              ))}
            </div>

            <div className={styles.addAccountSection}>
              {showAddPerm ? (
                <div className={styles.addFieldForm}>
                  <div className={styles.permCheckList}>
                    {availablePerms.length === 0 ? (
                      <span className={styles.noPerms}>No hay más permisos disponibles</span>
                    ) : (
                      availablePerms.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className={pendingPermIds.has(p.id) ? styles.permCheckItemOn : styles.permCheckItem}
                          onClick={() => togglePending(p.id)}
                        >
                          <div className={styles.permCheckItemLeft}>
                            <span>{p.name}</span>
                            {p.is_system && <span className={styles.permSystemBadge}>Sistema</span>}
                          </div>
                          {pendingPermIds.has(p.id) && <SmallCheckIcon />}
                        </button>
                      ))
                    )}
                  </div>
                  <div className={styles.addFieldActions}>
                    <button
                      type="button"
                      className={styles.saveBtn}
                      onClick={handleAddSelected}
                      disabled={pendingPermIds.size === 0}
                    >
                      {pendingPermIds.size > 0 ? `Agregar (${pendingPermIds.size})` : "Agregar"}
                    </button>
                    <button
                      type="button"
                      className={styles.editBtn}
                      onClick={() => { setShowAddPerm(false); setPendingPermIds(new Set()); }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : availablePerms.length > 0 ? (
                <button
                  type="button"
                  className={styles.addAccountBtn}
                  onClick={() => setShowAddPerm(true)}
                >
                  <AddIcon className={styles.addIcon} />
                  Agregar permiso
                </button>
              ) : null}
            </div>
          </div>
        )}

        {actionError && (
          <div onClick={(e) => e.stopPropagation()}>
            <FloatingModal direction="column">
              <span role="alert" style={{ whiteSpace: "pre-line" }}>
                {actionError}
              </span>
              <Button onClick={() => setActionError(null)}>OK</Button>
            </FloatingModal>
          </div>
        )}

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={saving || !name.trim()}
          >
            {saving
              ? (isCreate ? "Creando..." : "Guardando...")
              : (isCreate ? "Crear grupo" : "Guardar cambios")}
          </button>
        </div>
      </div>

      {!isCreate && showDeleteConfirm && (
        <ConfirmDeleteModal
          title="¿Eliminar este grupo?"
          description="Todos los usuarios perderán esta membresía."
          name={group?.name}
          onConfirm={handleDelete}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}
    </ModalOverlay>
  );
}

export function ConfirmDeleteModal({
  title,
  description,
  name,
  confirmLabel = "Eliminar definitivamente",
  confirmingLabel = "Eliminando...",
  onConfirm,
  onClose,
}) {
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm?.();
    } finally {
      setConfirming(false);
    }
  };

  return (
    <ModalOverlay className={styles.centerOverlay} onClose={onClose}>
      <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.confirmBody}>
          <div className={styles.confirmTitle}>{title}</div>
          {description && <div className={styles.confirmDescription}>{description}</div>}
          {name && <div className={styles.confirmName}>{name}</div>}
        </div>
        <div className={styles.confirmActions}>
          <button type="button" className={styles.confirmCancelBtn} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className={styles.confirmDeleteBtn}
            onClick={handleConfirm}
            disabled={confirming}
          >
            {confirming ? confirmingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
